import { resolveApiEnvironment } from "../native/apiEnvironment";

/**
 * Which OPFS namespace each principal and project last held on this device, so the file cache
 * (`loadFileAndCache`) can evict the folder a newer one supersedes. A service of its own because
 * a project's admission records into it as well as the viewer's cache.
 */
const SCOPE_NAMESPACE_INDEX_KEY = "pp_opfs_scope_namespaces";

export function scopeNamespaceIndexKey(principalId: string, projectId: string): string {
    // The namespace hash itself is a function of (origin, scopeKey); indexing
    // by the same origin keeps this consistent across local/staging/prod.
    return `${resolveApiEnvironment().origin}:${principalId}:${projectId}`;
}

export function readScopeNamespaceIndex(): Record<string, string> {
    try {
        const raw = window.localStorage.getItem(SCOPE_NAMESPACE_INDEX_KEY);
        return raw ? (JSON.parse(raw) as Record<string, string>) : {};
    } catch {
        return {};
    }
}

export function writeScopeNamespaceIndex(index: Record<string, string>): void {
    try {
        window.localStorage.setItem(SCOPE_NAMESPACE_INDEX_KEY, JSON.stringify(index));
    } catch {
        // Best-effort; a failed write only means the next role change re-evicts from the same stale entry.
    }
}

/**
 * A legacy-bound project's first canonical admission on this device supersedes the
 * folder its legacy tabs filled (`files/<legacyOrgSlug>`): the URL tab keeps its PDFs
 * under its own authority namespace, so that copy would otherwise stay in OPFS until
 * sign-out. Recorded as the namespace this principal and project held before, so the
 * next `cacheOperation` evicts it like any other superseded one. Once the index names a
 * namespace for the pair, that one is the previous and nothing is recorded.
 */
export function supersedeLegacyFileCache(
    principalId: string,
    projectId: string,
    legacyOrgSlug: string,
): void {
    const index = readScopeNamespaceIndex();
    const storageKey = scopeNamespaceIndexKey(principalId, projectId);
    if (index[storageKey]) return;
    index[storageKey] = legacyOrgSlug;
    writeScopeNamespaceIndex(index);
}
